import { MouseEvent } from 'react'

import { useAppContext } from 'aem-libs-react-ts/src/hooks/useAppContext'
import { StaticParamsBase } from 'aem-libs-react-ts/src/types/common'
import { CtaElementTarget, gaTracker } from 'aem-libs-react-ts/src/utils/gaTracker'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'

import { ButtonSelect } from '../../../../forms/button/button-select'
import { ButtonSelectItemType } from '../../../../forms/button/button-select/types'
import { showOnDevice } from './ResultCta'
import { ButtonWrapper, CtaLinkButton } from './ResultCta.styled'
import branchArrangeIco from './assets/branchArrange.svg'
import callMeIco from './assets/callMe.svg'
import georgeIco from './assets/george.svg'
import onlineIco from './assets/online.svg'
import { ResultButtonType, ResultCtaButtonType, ResultSelectItemType } from './types'


// eslint-disable-next-line @typescript-eslint/no-explicit-any
const icoMap: Record<ResultButtonType, any> = {
  george: georgeIco,
  branchArrange: branchArrangeIco,
  callMe: callMeIco,
  online: onlineIco
}

const trackingMap: Record<ResultButtonType, CtaElementTarget> = {
  george: 'clickPrimaryGeorgeEntrance',
  branchArrange: 'clickPrimaryBookingOfflineEntrance',
  callMe: 'clickPrimaryCMBEntrance',
  online: 'clickPrimaryNonGeorgeEntrance'
}

type ResultCtaButtonProps = ResultCtaButtonType

type ResultCtaButtonEventType = {
  buttonType: ResultButtonType
  deepLink?: boolean
}

type ValidResultSelectItemType = Omit<ResultSelectItemType, 'label' | 'href'> & { label: string; href: string }

const isLabeledResultSelectItem = (item: ResultSelectItemType): item is ValidResultSelectItemType => {
  return isDefined(item.label) && isDefined(item.href)
}

const mapButtonSelectItem = (
  item: ValidResultSelectItemType
): ButtonSelectItemType<{ buttonType: ResultButtonType; deepLink?: boolean }> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { showOnDevice, ...itemProps } = item
  return {
    ...itemProps,
    value: { buttonType: item.type, ...(item.isDeeplink && { deepLink: item.isDeeplink }) },
    icon: icoMap[item.type]
  }
}

export const ResultCtaButton = ({
  url,
  type,
  label,
  items,
  isDeeplink,
  testId,
  theme,
  onClick
}: ResultCtaButtonProps) => {
  const { paramsStatic } = useAppContext<StaticParamsBase>()
  const listButtonItems = items ?? []

  const handleButtonClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const resultButtonType = listButtonItems.at(0)?.type ?? type
    if (resultButtonType) {
      gaTracker.withVariant(paramsStatic.variant).onCtaClick({
        elementTarget: trackingMap[resultButtonType],
        ...(isDeeplink && { deepLink: isDeeplink })
      })
    }

    onClick?.(e)
  }

  const handleSelectTypeClick = (buttonEvent: ResultCtaButtonEventType, e: MouseEvent<HTMLAnchorElement>) => {
    gaTracker.withVariant(paramsStatic.variant).onCtaClick({
      elementTarget: trackingMap[buttonEvent.buttonType],
      ...(buttonEvent.deepLink && { deepLink: buttonEvent.deepLink })
    })

    onClick?.(e)
  }

  const showButton = () => {
    return (url && !listButtonItems?.length) || listButtonItems.filter((i) => i.showButton).length === 1
  }

  const getButtonData = () => {
    if (url && label) {
      return {
        url,
        label
      }
    }
    const item = listButtonItems
      .filter((i) => i.showButton)
      .filter(isLabeledResultSelectItem)
      .at(0)

    if (!item) {
      throw 'No items visible'
    }
    return {
      label: item.label,
      url: item.href
    }
  }

  const trackDropdownClick = (isOpen: boolean) => {
    if (isOpen) {
      gaTracker.withVariant(paramsStatic.variant).onCtaClick({ elementTarget: 'clickPrimaryGeorgeEntranceDropdown' })
    }
  }

  const usedTestId = testId ?? 'result-cta-button'

  return (
    <div data-testid={usedTestId}>
      {showButton() ? (
        <ButtonWrapper>
          <CtaLinkButton
            buttonTheme={theme ?? 'orange'}
            href={getButtonData().url}
            onClick={handleButtonClick}
            data-testid={createTestId(usedTestId, 'button')}
          >
            {getButtonData().label}
          </CtaLinkButton>
        </ButtonWrapper>
      ) : (
        <ButtonWrapper>
          <ButtonSelect
            buttonTheme={'orange'}
            label={label}
            items={[
              ...listButtonItems
                .filter((i) => i.showButton && showOnDevice(i))
                .filter(isLabeledResultSelectItem)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map(mapButtonSelectItem)
            ]}
            onClick={handleSelectTypeClick}
            onSwitchItemsVisibility={trackDropdownClick}
            testId={createTestId(usedTestId, 'button-select')}
          />
        </ButtonWrapper>
      )}
    </div>
  )
}
